import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface ChampaignCountyTextParserProps {
  onImportComplete: () => void;
}

export function ChampaignCountyTextParser({ onImportComplete }: ChampaignCountyTextParserProps) {
  const [pastedText, setPastedText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const parseRecords = (text: string) => {
    const records = [];
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    console.log('Total lines to parse:', lines.length);
    
    let i = 0;
    while (i < lines.length) {
      // Skip standalone numbers like "2", "3", etc. (list numbers)
      if (/^\d{1,3}$/.test(lines[i]) && lines[i].length <= 3) {
        i++;
        continue;
      }

      // Document number pattern: 202200000990 (12 digits)
      if (/^\d{12}$/.test(lines[i])) {
        const documentNumber = lines[i];
        console.log('Found document number:', documentNumber);
        i++;
        
        // Skip book/page numbers (should be 2-3 short number lines)
        while (i < lines.length && /^\d{1,5}$/.test(lines[i]) && lines[i].length <= 5) {
          i++;
        }
        
        // Document type (should be LAND CONTRACT or similar)
        const documentType = lines[i] || '';
        console.log('Document type:', documentType);
        i++;
        
        let grantorName = '';
        let granteeName = '';
        let filingDate = '';
        let propertyDescription = '';
        
        // Parse grantor (R section)
        if (i < lines.length && lines[i] === 'R') {
          i++;
          const grantorLines = [];
          while (i < lines.length && lines[i] !== 'E' && !/^\d{2}\/\d{2}\/\d{4}$/.test(lines[i])) {
            grantorLines.push(lines[i]);
            i++;
          }
          grantorName = grantorLines.join(' ');
        }
        
        // Parse grantee (E section)
        if (i < lines.length && lines[i] === 'E') {
          i++;
          const granteeLines = [];
          while (i < lines.length && !/^\d{2}\/\d{2}\/\d{4}$/.test(lines[i])) {
            granteeLines.push(lines[i]);
            i++;
          }
          granteeName = granteeLines.join(' ');
        }
        
        // Date: MM/DD/YYYY
        if (i < lines.length && /^\d{2}\/\d{2}\/\d{4}$/.test(lines[i])) {
          filingDate = lines[i];
          i++;
        }
        
        // Property description (everything until next document or View/end)
        const propertyLines = [];
        while (i < lines.length && 
               lines[i] !== 'View' && 
               !/^\d{12}$/.test(lines[i]) &&
               !/^\d{1,3}$/.test(lines[i])) {
          propertyLines.push(lines[i]);
          i++;
        }
        propertyDescription = propertyLines.join(' | ');
        
        // Skip "View" if present
        if (i < lines.length && lines[i] === 'View') {
          i++;
        }
        
        // Only save if it's a land contract
        if (documentType.toUpperCase().includes('LAND CONTRACT')) {
          console.log('Creating record for:', documentNumber);
          records.push({
            document_number: documentNumber,
            document_type: documentType,
            grantor_name: grantorName,
            grantee_name: granteeName,
            filing_date: filingDate ? new Date(filingDate).toISOString().split('T')[0] : null,
            property_address: propertyDescription || null,
            legal_description: propertyDescription || null,
            county: 'champaign',
            is_potential_owner_finance: true,
            confidence_score: 95,
            address_needs_review: true,
            grantee_address: null
          });
        } else {
          console.log('Skipping non-land-contract:', documentType);
        }
      } else {
        i++;
      }
    }
    
    console.log('Total records parsed:', records.length);
    return records;
  };

  const handleImport = async () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some text to import");
      return;
    }

    setIsProcessing(true);

    try {
      const records = parseRecords(pastedText);

      if (records.length === 0) {
        toast.error("No valid records found in the pasted text");
        setIsProcessing(false);
        return;
      }

      console.log('Attempting to insert', records.length, 'records');
      
      const { data, error } = await supabase
        .from('county_research')
        .insert(records)
        .select();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      console.log('Successfully inserted', data?.length || 0, 'records');
      toast.success(`Successfully imported ${data?.length || records.length} records`);
      setPastedText("");
      onImportComplete();
    } catch (error) {
      console.error("Error importing records:", error);
      toast.error("Failed to import records");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Champaign County Text Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground mb-2">
            Paste the land contract records from Champaign County recorder's system:
          </p>
          <Textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste the full text output here..."
            className="min-h-[400px] font-mono text-xs"
          />
        </div>

        <Button 
          onClick={handleImport} 
          disabled={isProcessing || !pastedText.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Import Records'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
